import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Check } from "lucide-react";

export default function AdminMedia() {
  const { authedClient } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/uploads");
      setAssets(data);
    } catch {
      toast.error("Could not load media library");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (label) fd.append("label", label);
    try {
      const { data } = await authedClient().post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAssets((prev) => [data, ...prev]);
      setLabel("");
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Delete ${asset.original_filename}?`)) return;
    try {
      await authedClient().delete(`/admin/uploads/${asset.id}`);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyUrl = (asset) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${asset.public_url}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(asset.id);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  return (
    <div data-testid="admin-media-page">
      <div className="mb-6">
        <p className="eyebrow mb-2">★ ADMIN</p>
        <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Media Library</h1>
        <p className="text-sm text-[color:var(--ink-soft)] mt-1">
          Upload photos and flyers once. Reuse them across events and pages.
        </p>
      </div>

      <div className="bg-[color:var(--cream)] rounded-2xl p-5 mb-8 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">
            Optional label
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Passover seder flyer"
            data-testid="admin-media-label"
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white"
          />
        </div>
        <label className="btn-primary cursor-pointer">
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading…" : "Upload file"}
          <input
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={upload}
            disabled={uploading}
            data-testid="admin-media-upload-input"
          />
        </label>
      </div>

      {loading ? (
        <p className="text-center text-[color:var(--muted)] py-12">Loading…</p>
      ) : assets.length === 0 ? (
        <p className="text-center text-[color:var(--muted)] py-12">
          No uploads yet. Drop in a flyer or photo above to get started.
        </p>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="admin-media-grid"
        >
          {assets.map((a) => (
            <div
              key={a.id}
              className="border border-[color:var(--line)] rounded-xl overflow-hidden bg-white"
              data-testid={`admin-media-card-${a.id}`}
            >
              <div className="aspect-video bg-[color:var(--cream)] flex items-center justify-center">
                {a.content_type?.startsWith("image/") ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${a.public_url}`}
                    alt={a.label || a.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-sm text-[color:var(--muted)]">{a.content_type}</span>
                )}
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-[color:var(--blue-ink)] truncate">
                      {a.label || a.original_filename}
                    </p>
                    <p className="text-xs text-[color:var(--muted)] truncate">
                      {Math.round(a.size / 1024)} KB · {a.content_type}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => copyUrl(a)}
                    data-testid={`admin-media-copy-${a.id}`}
                    className="flex-1 text-xs flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[color:var(--line)] hover:bg-[color:var(--cream)] transition"
                  >
                    {copiedId === a.id ? (
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                    Copy URL
                  </button>
                  <button
                    onClick={() => remove(a)}
                    data-testid={`admin-media-delete-${a.id}`}
                    className="px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
